import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';

const CYCLE_DURATION_MS = 5000;

export default function FocusMenuHero() {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const panelRef = useRef(null);

  const remainingMsRef = useRef(CYCLE_DURATION_MS);
  const startTimeRef = useRef(Date.now());
  const timerRef = useRef(null);

  const activeItem = t.disciplines[activeIndex] || t.disciplines[0];

  const handleSelect = useCallback((idx) => {
    remainingMsRef.current = CYCLE_DURATION_MS;
    startTimeRef.current = Date.now();
    setActiveIndex(idx);
  }, []);

  // When activeIndex changes: reset full remaining duration
  useEffect(() => {
    remainingMsRef.current = CYCLE_DURATION_MS;
    startTimeRef.current = Date.now();
  }, [activeIndex]);

  // Precise Pause & Resume tracking without resetting progress
  useEffect(() => {
    if (isPaused) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const elapsed = Date.now() - startTimeRef.current;
      remainingMsRef.current = Math.max(0, remainingMsRef.current - elapsed);
    } else {
      startTimeRef.current = Date.now();
      timerRef.current = setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % t.disciplines.length);
      }, remainingMsRef.current);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [activeIndex, isPaused, t.disciplines.length]);

  // Specular Mouse Spotlight Sheen on Panel
  const handleMouseMove = (e) => {
    if (!panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    panelRef.current.style.setProperty('--mouse-x', `${x}px`);
    panelRef.current.style.setProperty('--mouse-y', `${y}px`);
  };

  return (
    <section className="hero-neo-section reveal-on-scroll" id="top">
      <div className="container">
        {/* Top Identification Header */}
        <div className="hero-neo-topbar">
          <div>
            <div className="neo-tag-pill pulse-badge">
              <span className="dot-live-acid"></span>
              <span>{t.personalInfo.status}</span>
            </div>
            <h1 className="hero-neo-name">
              <span className="chrome-title">{t.personalInfo.name}</span>
            </h1>
            <div className="hero-neo-role">
              {t.personalInfo.role} <span style={{ color: '#525866' }}>{'//'}</span> {t.personalInfo.tagline}
            </div>
          </div>

          <div className="hero-neo-meta-box">
            <div className="meta-row-neo">
              <span className="meta-key">LOCATION:</span>
              <span className="meta-val">{t.personalInfo.location}</span>
            </div>
            <div className="meta-row-neo">
              <span className="meta-key">FOCUS:</span>
              <span className="meta-val">{t.personalInfo.focus}</span>
            </div>
            <div className="meta-row-neo">
              <span className="meta-key">ACCREDITATION:</span>
              <span className="meta-val">ESOGÜ CENG · MICROSOFT MCPD</span>
            </div>
          </div>
        </div>

        {/* Stable Neobrutalist Focus Menu & Tree Bracket Console */}
        <div
          className="neo-console-layout"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Left Column: Stack of Selectable Uppercase Disciplines */}
          <div className="neo-focus-menu-stack" role="tablist">
            {t.disciplines.map((item, idx) => {
              const isSelected = activeIndex === idx;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  className={`neo-menu-item ${isSelected ? 'active' : ''}`}
                  onClick={() => handleSelect(idx)}
                  onMouseEnter={() => handleSelect(idx)}
                >
                  <span className="neo-item-index">0{idx + 1}</span>
                  <span className="neo-item-label">{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Center: Stable Connecting Tree Bracket */}
          <div className="neo-bracket-connector" aria-hidden="true">
            <svg
              className="bracket-svg-line"
              viewBox="0 0 36 280"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M 2 15 H 16 Q 24 15 24 35 V 125 Q 24 140 34 140 Q 24 140 24 155 V 245 Q 24 265 16 265 H 2"
                stroke="#282d3b"
                strokeWidth="2"
              />
              <path
                d="M 24 140 H 36"
                stroke="#ccff00"
                strokeWidth="2.5"
              />
            </svg>
            <div className="bracket-node-dot"></div>
          </div>

          {/* Right Column: Specimen Dossier & Telemetry Content Box */}
          <div
            ref={panelRef}
            className="neo-specimen-panel spotlight-card"
            onMouseMove={handleMouseMove}
          >
            {/* Seamless Pause & Resume Progress Indicator */}
            <div
              key={activeIndex}
              className="ticker-progress-bar"
              style={{
                animation: `tickerFill ${CYCLE_DURATION_MS}ms linear forwards`,
                animationPlayState: isPaused ? 'paused' : 'running',
              }}
            />

            <div className="panel-status-header">
              <span className="status-code">{t.ui.interactiveConsoleTitle}</span>
              <span className="status-badge-mono">
                [SPECIMEN 0{activeIndex + 1}/0{t.disciplines.length}]
              </span>
            </div>

            <div key={activeItem.id} className="specimen-main-content animate-fade-in">
              <div className="specimen-sublabel">{activeItem.sublabel}</div>
              <h2 className="specimen-headline">{activeItem.title}</h2>
              <p className="specimen-description-text">{activeItem.desc}</p>
            </div>

            <div className="specimen-panel-footer">
              <div className="acid-badge-block">{activeItem.badge}</div>

              <a href={activeItem.link} className="btn-neo btn-neo-chrome btn-sweep">
                <span>{t.ui.exploreSpecimen}</span>
                <i className="fa-solid fa-arrow-down" style={{ fontSize: '0.75rem' }}></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
